/**
 * Firmware Deployments Tab — OS Patch → Deployment Groups → Firmware
 *
 * Phase 4 (Firmware Deployment build). Wraps the OEM BIOS payload
 * referenced by ``firmware_versions.download_url`` into a Win32 Intune
 * application (via existing Apps Management packaging pipeline) and
 * assigns it to an Azure AD group targeting the affected devices.
 *
 * Two entry-points feed this tab:
 *   1. "New Firmware Deployment" wizard in this tab. Admin picks
 *      manufacturer → model_family (optionally multiple in bulk mode) →
 *      target_versions (auto-populated from the missing_one /
 *      missing_multiple buckets) → preview device list → deploy.
 *   2. Per-device "Package & Deploy" button on the Reports → Firmware
 *      drill-in table (single-device deployment; per_device=true).
 *
 * Owned data:
 *   • GET  /api/os-patch/firmware/deployments
 *   • POST /api/os-patch/firmware/deployments/preview
 *   • POST /api/os-patch/firmware/deployments
 *   • POST /api/os-patch/firmware/deployments/{id}/retry
 *   • DELETE /api/os-patch/firmware/deployments/{id}
 */
import React, { useState, useEffect, useMemo } from 'react';
import {
  HardDrive, Cpu, RefreshCw, Rocket, X, Trash2, AlertTriangle,
  CheckCircle, Clock, Package as PackageIcon, ChevronDown, ChevronRight,
  Users, Download, Shield, FileCode2, Copy as CopyIcon, BarChart3, UserPlus,
  Undo2, FileText, Trash, PauseCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '@/App';
import { usePermissions } from '@/contexts/PermissionsContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Small helper that returns the Authorization header for the current
// signed-in user. Mirrors the pattern used throughout the app
// (PackagesPage.jsx, AppsDashboard.jsx, etc.) — the ALICE codebase
// doesn't set a global axios auth interceptor, so every caller
// attaches the Bearer manually.
async function authHeaders(getAccessToken) {
  if (!getAccessToken) return {};
  try {
    const token = await getAccessToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch {
    return {};
  }
}

// Format a UTC ISO timestamp as a short relative label.
function fmtRelative(iso) {
  if (!iso) return '—';
  try {
    const then = new Date(iso).getTime();
    const now = Date.now();
    const diff = Math.max(0, Math.floor((now - then) / 1000));
    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  } catch {
    return iso;
  }
}

// Chip colours for the deployment status pill.
const STATUS_STYLE = {
  queued: { bg: 'bg-slate-500/20', text: 'text-slate-300', label: 'Queued', icon: Clock },
  downloading: { bg: 'bg-blue-500/20', text: 'text-blue-300', label: 'Downloading', icon: Download },
  packaging: { bg: 'bg-cyan-500/20', text: 'text-cyan-300', label: 'Packaging', icon: PackageIcon },
  uploading_to_intune: { bg: 'bg-purple-500/20', text: 'text-purple-300', label: 'Uploading to Intune', icon: Rocket },
  assigning: { bg: 'bg-amber-500/20', text: 'text-amber-300', label: 'Assigning', icon: Users },
  completed: { bg: 'bg-emerald-500/20', text: 'text-emerald-300', label: 'Completed', icon: CheckCircle },
  failed: { bg: 'bg-rose-500/20', text: 'text-rose-300', label: 'Failed', icon: AlertTriangle },
  cancelled: { bg: 'bg-slate-500/20', text: 'text-slate-400', label: 'Cancelled', icon: X },
};

function StatusPill({ status }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.queued;
  const Icon = s.icon;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${s.bg} ${s.text}`}
      data-testid={`firmware-deployment-status-${status}`}
    >
      <Icon className="w-3 h-3" /> {s.label}
    </span>
  );
}

// Stage pill — Test / Phase 1 / Phase 2 / Phase 3 / LIVE. Reads
// deployment.phase_slot (preferred) + deployment.phase (numeric).
function StagePill({ deployment }) {
  const slot = deployment.phase_slot;
  const goLive = deployment.go_live_status;
  if (goLive === 'live') {
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-purple-500/20 text-purple-300"><Rocket className="w-3 h-3" /> LIVE</span>;
  }
  const label = { test: 'Test', phase1: 'Phase 1', phase2: 'Phase 2', phase3: 'Phase 3' }[slot];
  if (!label) return null;
  const cls = slot === 'test'
    ? 'bg-purple-500/20 text-purple-300'
    : slot === 'phase3'
      ? 'bg-emerald-500/20 text-emerald-300'
      : 'bg-blue-500/20 text-blue-300';
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}
      data-testid={`firmware-deployment-stage-${deployment.id}`}
    >
      {label}
    </span>
  );
}

// Device-progress mini-bar — Installed/Pending/Deferred/Failed from Intune.
function DeviceProgressBar({ stats }) {
  const s = stats || {};
  const total = Math.max(1, s.total || 0);
  const pct = (n) => Math.round(((n || 0) / total) * 100);
  return (
    <div className="min-w-[180px]" data-testid="firmware-deployment-progress">
      <div className="flex items-center justify-between text-[11px] mb-0.5 font-mono">
        <span className="text-emerald-300">{s.installed || 0} inst</span>
        <span className="text-amber-300">{s.pending || 0} pend</span>
        {(s.deferred || 0) > 0 && (
          <span className="text-sky-300" data-testid="firmware-progress-deferred">{s.deferred} defer</span>
        )}
        <span className="text-rose-300">{s.failed || 0} fail</span>
        <span className="text-white/60">/ {s.total || 0}</span>
      </div>
      <div className="w-full h-2 bg-white/5 rounded-full overflow-hidden flex">
        <div className="bg-emerald-400 h-full" style={{ width: `${pct(s.installed)}%` }} />
        <div className="bg-amber-400 h-full" style={{ width: `${pct(s.pending)}%` }} />
        <div className="bg-sky-400 h-full" style={{ width: `${pct(s.deferred)}%` }} />
        <div className="bg-rose-400 h-full" style={{ width: `${pct(s.failed)}%` }} />
      </div>
    </div>
  );
}

// ─────────────────────────── deployment row ───────────────────────────
function DeploymentRow({ deployment, onRefresh, onDelete, getAccessToken }) {
  const [expanded, setExpanded] = useState(false);
  const [busy, setBusy] = useState(false);
  // Read-only OS Patch tenants: hide Retry / Delete.
  const { canWriteOSPatch } = usePermissions();

  const handleRetry = async () => {
    setBusy(true);
    try {
      const headers = await authHeaders(getAccessToken);
      await axios.post(
        `${API}/os-patch/firmware/deployments/${deployment.id}/retry`,
        {},
        { headers },
      );
      toast.success('Deployment re-queued');
      onRefresh?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Retry failed');
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete deployment "${deployment.display_name}"?\n\n(This does NOT remove the Intune app or Azure AD group; use Intune to remove those if needed.)`)) return;
    setBusy(true);
    try {
      const headers = await authHeaders(getAccessToken);
      await axios.delete(
        `${API}/os-patch/firmware/deployments/${deployment.id}`,
        { headers },
      );
      toast.success('Deployment deleted');
      onDelete?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    } finally {
      setBusy(false);
    }
  };

  const progress = Math.max(0, Math.min(100, deployment.progress || 0));

  return (
    <Card
      className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border-[rgba(229,239,250,0.1)]"
      data-testid={`firmware-deployment-row-${deployment.id}`}
    >
      <CardContent className="p-0">
        <div
          className="p-5 flex items-center justify-between cursor-pointer hover:bg-[rgba(229,239,250,0.06)]"
          onClick={() => setExpanded((v) => !v)}
        >
          <div className="flex items-center gap-3 min-w-0 flex-1">
            {expanded ? <ChevronDown className="w-5 h-5 text-white/70" /> : <ChevronRight className="w-5 h-5 text-white/70" />}
            <Cpu className="w-5 h-5 text-cyan-400 shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span
                  className="text-white font-medium truncate"
                  data-testid={`firmware-deployment-name-${deployment.id}`}
                  title={deployment.display_name}
                >
                  {deployment.display_name}
                </span>
                <StatusPill status={deployment.status} />
                <StagePill deployment={deployment} />
                {deployment.per_device && (
                  <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-purple-500/20 text-purple-300">
                    per-device
                  </span>
                )}
                {(deployment.manufacturer || '').toLowerCase().startsWith('microsoft') && (
                  <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-500/20 text-amber-300 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" /> driver+firmware bundle
                  </span>
                )}
              </div>
              <div className="text-white/70 text-xs mt-1 flex flex-wrap gap-x-4">
                <span>{deployment.manufacturer} · {deployment.model_family}</span>
                <span>Target BIOS: <span className="font-mono text-white/90">{deployment.target_version || '—'}</span></span>
                <span>{deployment.device_count ?? 0} device{deployment.device_count === 1 ? '' : 's'}</span>
                <span>Created: {fmtRelative(deployment.created_at)}</span>
                {deployment.created_by && <span>by {deployment.created_by}</span>}
                {deployment.assignment_intent && (
                  <span
                    className="text-cyan-300"
                    data-testid={`firmware-deployment-intent-${deployment.id}`}
                  >
                    Intent: <span className="font-mono">{deployment.assignment_intent}</span>
                    {deployment.assignment_intent === 'available_with_deadline' && deployment.deadline_datetime && (
                      <> (deadline {deployment.deadline_datetime})</>
                    )}
                    {deployment.assignment_intent === 'psadt_deferral' && deployment.psadt_deadline_at && (
                      <> (PSADT {deployment.psadt_deadline_at}, Intune backstop {deployment.intune_deadline_at})</>
                    )}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            {deployment.device_stats && (deployment.device_stats.total > 0) && (
              <DeviceProgressBar stats={deployment.device_stats} />
            )}
            {deployment.completed_at && (
              <div className="text-xs text-white/60 min-w-[70px] text-right hidden md:block">
                <div className="text-white/40 uppercase tracking-wide text-[9px]">Deployed</div>
                <div className="text-white/90">{fmtRelative(deployment.completed_at)}</div>
              </div>
            )}
            {canWriteOSPatch && (deployment.status === 'failed' || deployment.status === 'cancelled') && (
              <Button
                variant="outline"
                size="sm"
                onClick={(e) => { e.stopPropagation(); handleRetry(); }}
                disabled={busy}
                className="border-amber-400 text-amber-300 hover:bg-amber-500/10 h-8"
                data-testid={`firmware-deployment-retry-${deployment.id}`}
              >
                <RefreshCw className={`w-3.5 h-3.5 ${busy ? 'animate-spin' : ''}`} />
              </Button>
            )}
            {canWriteOSPatch && (
              <Button
                variant="outline"
                size="sm"
                onClick={(e) => { e.stopPropagation(); handleDelete(); }}
                disabled={busy}
                className="border-rose-400 text-rose-300 hover:bg-rose-500/10 h-8"
                data-testid={`firmware-deployment-delete-${deployment.id}`}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            )}
          </div>
        </div>
        {expanded && (
          <div className="border-t border-[rgba(229,239,250,0.1)] p-5 space-y-3 bg-[rgba(229,239,250,0.03)]">
            {/* Progress bar */}
            <div>
              <div className="flex items-center justify-between text-xs mb-1">
                <span className="text-white/80">{deployment.current_step || 'Idle'}</span>
                <span className="text-white/60 font-mono">{progress}%</span>
              </div>
              <div className="w-full h-1.5 bg-white/5 rounded-full overflow-hidden">
                <div
                  className={`h-full transition-all ${
                    deployment.status === 'failed'
                      ? 'bg-rose-400'
                      : deployment.status === 'completed'
                        ? 'bg-emerald-400'
                        : 'bg-cyan-400'
                  }`}
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>

            {/* Key metadata grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
              <div>
                <div className="text-white/50 uppercase tracking-wide">Downloaded</div>
                <div className="text-white/90 font-mono">
                  {deployment.downloaded_size_bytes
                    ? `${(deployment.downloaded_size_bytes / (1024 * 1024)).toFixed(1)} MB`
                    : '—'}
                </div>
              </div>
              <div>
                <div className="text-white/50 uppercase tracking-wide">SHA256</div>
                <div className="text-white/90 font-mono truncate" title={deployment.downloaded_sha256 || ''}>
                  {(deployment.downloaded_sha256 || '').slice(0, 16) || '—'}
                </div>
              </div>
              <div>
                <div className="text-white/50 uppercase tracking-wide">Intune App</div>
                <div className="text-white/90 font-mono truncate" title={deployment.intune_app_id || ''}>
                  {deployment.intune_app_id || '—'}
                </div>
              </div>
              <div>
                <div className="text-white/50 uppercase tracking-wide">Group</div>
                <div className="text-white/90 truncate" title={deployment.azure_ad_group_name || ''}>
                  {deployment.azure_ad_group_name || '—'}
                </div>
              </div>
            </div>

            {deployment.assignment_result && (
              <div className="text-xs text-white/70">
                Group membership: <span className="text-emerald-300 font-medium">{deployment.assignment_result.success}</span>
                {' / '}
                {deployment.assignment_result.total} added
                {deployment.assignment_result.failed > 0 && (
                  <span className="text-rose-300"> ({deployment.assignment_result.failed} failed)</span>
                )}
              </div>
            )}

            {deployment.error_message && (
              <div className="rounded bg-rose-500/10 border border-rose-500/30 p-2 text-xs text-rose-200 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                <span>{deployment.error_message}</span>
              </div>
            )}

            {/* Log lines (bounded, most-recent-last) */}
            {deployment.log_lines?.length > 0 && (
              <div>
                <div className="text-white/50 text-xs uppercase tracking-wide mb-1">Log</div>
                <div className="max-h-40 overflow-auto rounded bg-black/30 p-2 space-y-1 font-mono text-[11px]">
                  {deployment.log_lines.map((l, i) => (
                    <div key={i} className={`flex gap-2 ${l.level === 'error' ? 'text-rose-300' : l.level === 'warning' ? 'text-amber-300' : 'text-white/70'}`}>
                      <span className="text-white/40 shrink-0">{fmtRelative(l.ts)}</span>
                      <span className="uppercase text-[9px] tracking-wider shrink-0">{l.level}</span>
                      <span className="break-words">{l.message}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ─────────────────────────── new-deployment wizard ───────────────────────────
export function FirmwareDeploymentWizard({ onClose, onCreated, initialSelection = null }) {
  const { getAccessToken } = useAuth();
  // initialSelection = { manufacturer, model_family, target_version, per_device, device_id, device_name }
  const isSingleDevice = !!initialSelection?.per_device;

  // Bulk mode: allow picking multiple (manufacturer, model_family) tuples.
  const [bulkMode, setBulkMode] = useState(false);
  const [filterOptions, setFilterOptions] = useState({ manufacturers: [], model_families: [] });
  const [selection, setSelection] = useState(
    initialSelection
      ? [{ manufacturer: initialSelection.manufacturer, model_family: initialSelection.model_family, target_version: initialSelection.target_version || '' }]
      : [{ manufacturer: '', model_family: '', target_version: '' }],
  );
  const [statusFilter, setStatusFilter] = useState(['missing_one', 'missing_multiple']);
  const [previews, setPreviews] = useState({}); // key = idx → preview payload
  const [previewLoading, setPreviewLoading] = useState(false);
  const [deploying, setDeploying] = useState(false);
  const [msConfirmed, setMsConfirmed] = useState(!!initialSelection?.ms_confirm_accepted);

  // 2026-07-24 (Phase 5, Workstream D) — the wizard now defaults to
  // the PSADT-driven Install/Defer UX (branded prompt, 3 deferrals,
  // 24h spacing, HKLM-backed counter). Intune is set to Required
  // with a `deadlineDateTime` 24h after the PSADT deadline as the
  // safety-net backstop. The three Phase 4 intents (`available`,
  // `available_with_deadline`, `required`) remain valid on the
  // backend for admins hitting the API directly, but this wizard
  // no longer surfaces them — that was the customer's explicit ask.
  const [assignmentIntent, setAssignmentIntent] = useState('psadt_deferral');
  // Auto-computed by the backend when psadt_deferral is submitted
  // without explicit deadlines — but we let the admin override them
  // via the two datetime-local pickers below (both default to
  // "T+3 days @ 16:00" / "T+4 days @ 16:00" in the admin's local TZ).
  const [psadtDeadlineAt, setPsadtDeadlineAt] = useState('');
  const [intuneDeadlineAt, setIntuneDeadlineAt] = useState('');
  const [deferIntervalHours, setDeferIntervalHours] = useState(24);
  const [deadlineDateTime, setDeadlineDateTime] = useState(''); // legacy `available_with_deadline` (unused by default UI)
  const [gracePresetMinutes, setGracePresetMinutes] = useState('none');
  const [customGraceMinutes, setCustomGraceMinutes] = useState('');
  const [allowSnooze, setAllowSnooze] = useState(true);
  const [snoozeMinutes, setSnoozeMinutes] = useState('60');
  const [countdownMinutes, setCountdownMinutes] = useState('15');

  // "Preview PSADT script" panel state (2026-07-23). Wizard-time
  // convenience — admin can eyeball the rendered
  // Invoke-AppDeployToolkit.ps1 (metadata + install block + uninstall)
  // before committing to Deploy. Backed by
  // POST /api/os-patch/firmware/deployments/preview-psadt.
  const [psadtPreviewOpen, setPsadtPreviewOpen] = useState(false);
  const [psadtPreviewLoading, setPsadtPreviewLoading] = useState(false);
  const [psadtPreview, setPsadtPreview] = useState(null);
  const [psadtPreviewError, setPsadtPreviewError] = useState('');
  const [psadtPreviewTab, setPsadtPreviewTab] = useState('injected'); // 'injected' | 'full'

  // 2026-07-24 (Workstream D) — prefill the PSADT + Intune deadline
  // pickers with sane defaults (T+3 / T+4 days @ 16:00 local). Admin
  // can override; leaving empty makes the backend auto-compute.
  useEffect(() => {
    if (assignmentIntent !== 'psadt_deferral') return;
    if (psadtDeadlineAt && intuneDeadlineAt) return;
    const toLocalInput = (d) => {
      const pad = (n) => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    };
    const now = new Date();
    const psadt = new Date(now);
    psadt.setDate(psadt.getDate() + 3);
    psadt.setHours(16, 0, 0, 0);
    const intune = new Date(now);
    intune.setDate(intune.getDate() + 4);
    intune.setHours(16, 0, 0, 0);
    if (!psadtDeadlineAt) setPsadtDeadlineAt(toLocalInput(psadt));
    if (!intuneDeadlineAt) setIntuneDeadlineAt(toLocalInput(intune));
  }, [assignmentIntent, psadtDeadlineAt, intuneDeadlineAt]);

  const anyMicrosoft = useMemo(
    () => selection.some((r) => (r.manufacturer || '').toLowerCase().startsWith('microsoft')),
    [selection],
  );

  useEffect(() => {
    (async () => {
      try {
        const headers = await authHeaders(getAccessToken);
        const res = await axios.get(
          `${API}/os-patch/firmware/devices/filter-options`,
          { headers },
        );
        // Backend contract (os_patch_routes.py get_firmware_devices_filter_options):
        //   { manufacturers: [str], models: [{ manufacturer, model }] }
        // The `model` value here is the catalog-resolved family, matching the
        // Reports drill-in label — see os_patch_routes.py:14488.
        const mfrs = res.data?.manufacturers || [];
        const models = res.data?.models || res.data?.model_families || [];
        setFilterOptions({
          manufacturers: mfrs,
          model_families: models.map((p) => ({
            manufacturer: p.manufacturer,
            model_family: p.model_family || p.model || p.family || p.name,
          })),
        });
        if (!mfrs.length) {
          // Surface an empty response so we don't silently render blank
          // dropdowns — the admin will see the toast and know the
          // endpoint returned no data (usually a tenant-routing or
          // Windows-device-filter miss on the backend).
          toast.info('Firmware filter options came back empty.');
        }
      } catch (e) {
        // Bubble the real failure to the user so we don't silently
        // render blank dropdowns.
        toast.error(
          `Failed to load Manufacturer/Model list: ${
            e?.response?.data?.detail || e?.message || 'unknown error'
          } (status ${e?.response?.status ?? '?'})`
        );
      }
    })();
  }, [getAccessToken]);

  const familiesFor = (mfr) => {
    if (!mfr) return [];
    const raw = filterOptions.model_families || [];
    const known = raw
      .filter((f) => (f.manufacturer || '').toLowerCase() === mfr.toLowerCase())
      .map((f) => f.model_family || f.family || f.name)
      .filter(Boolean);
    // Defensive: ensure any pre-selected initialSelection model_family
    // (from the per-device flow, which passes the raw row value) is
    // always renderable as an option, even if the filter-options
    // endpoint hasn't returned it (empty tenant, still-loading, or the
    // catalog resolver hasn't seen this family yet).
    if (initialSelection?.model_family
        && (initialSelection.manufacturer || '').toLowerCase() === mfr.toLowerCase()
        && !known.some((k) => k.toLowerCase() === initialSelection.model_family.toLowerCase())) {
      known.unshift(initialSelection.model_family);
    }
    return known;
  };

  // Manufacturer options — computed inline (no useMemo) so a stale
  // closure over an empty initial state can't render the dropdown
  // blank after the filter-options fetch has completed.
  const manufacturerOptions = (() => {
    const known = filterOptions.manufacturers || [];
    if (initialSelection?.manufacturer
        && !known.some((m) => (m || '').toLowerCase() === initialSelection.manufacturer.toLowerCase())) {
      return [initialSelection.manufacturer, ...known];
    }
    return known;
  })();

  const updateRow = (idx, field, value) => {
    setSelection((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      if (field === 'manufacturer') next[idx].model_family = '';
      return next;
    });
    setPreviews((p) => ({ ...p, [idx]: null }));
  };

  const addRow = () => setSelection((prev) => [...prev, { manufacturer: '', model_family: '', target_version: '' }]);
  const removeRow = (idx) => setSelection((prev) => prev.filter((_, i) => i !== idx));

  const handlePreview = async () => {
    setPreviewLoading(true);
    const nextPreviews = {};
    try {
      const headers = await authHeaders(getAccessToken);
      for (let i = 0; i < selection.length; i += 1) {
        const row = selection[i];
        if (!row.manufacturer || !row.model_family) continue;
        try {
          const res = await axios.post(
            `${API}/os-patch/firmware/deployments/preview`,
            {
              manufacturer: row.manufacturer,
              model_family: row.model_family,
              target_versions: row.target_version ? [row.target_version] : null,
              status_filter: statusFilter,
              device_ids: initialSelection?.device_id ? [initialSelection.device_id] : null,
            },
            { headers },
          );
          nextPreviews[i] = res.data;
          // Auto-fill target_version from catalog head if user hasn't picked one.
          if (!row.target_version && res.data?.latest_version) {
            setSelection((prev) => {
              const next = [...prev];
              next[i] = { ...next[i], target_version: res.data.latest_version };
              return next;
            });
          }
        } catch (e) {
          toast.error(`Preview failed for ${row.manufacturer} ${row.model_family}: ${e?.response?.data?.detail || e.message}`);
        }
      }
      setPreviews(nextPreviews);
    } finally {
      setPreviewLoading(false);
    }
  };

  const totalDevices = useMemo(
    () => Object.values(previews).reduce((sum, p) => sum + (p?.devices?.length || 0), 0),
    [previews],
  );

  // POST /firmware/deployments/preview-psadt using the first row that
  // has resolved manufacturer + model_family + target_version. Uses the
  // preview response's ``latest_version`` when the row hasn't been
  // manually overridden. Pops the modal open on success.
  const handlePreviewPsadt = async () => {
    setPsadtPreviewError('');
    const row = selection[0];
    const preview0 = previews[0];
    const targetVersion = (row?.target_version || preview0?.latest_version || '').trim();
    const manufacturer = (row?.manufacturer || '').trim();
    const modelFamily = (row?.model_family || '').trim();
    if (!manufacturer || !modelFamily || !targetVersion) {
      toast.error('Set manufacturer, model family, and target version (run Preview) first.');
      return;
    }
    setPsadtPreviewLoading(true);
    setPsadtPreviewOpen(true);
    try {
      const headers = await authHeaders(getAccessToken);
      const installerFilename = (
        preview0?.latest_download_url
          ? preview0.latest_download_url.split('/').pop().split('?')[0]
          : ''
      ) || undefined;
      const res = await axios.post(
        `${API}/os-patch/firmware/deployments/preview-psadt`,
        {
          manufacturer,
          model_family: modelFamily,
          target_version: targetVersion,
          installer_filename: installerFilename,
        },
        { headers },
      );
      setPsadtPreview(res.data);
      setPsadtPreviewTab('injected');
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'PSADT preview failed.';
      setPsadtPreviewError(typeof msg === 'string' ? msg : JSON.stringify(msg));
      setPsadtPreview(null);
    } finally {
      setPsadtPreviewLoading(false);
    }
  };

  const copyToClipboard = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text || '');
      toast.success(`${label} copied to clipboard`);
    } catch {
      toast.error('Clipboard copy failed — browser may be blocking it.');
    }
  };

  const handleDeploy = async () => {
    if (anyMicrosoft && !msConfirmed) {
      toast.error('Microsoft Surface deployments require the explicit confirmation checkbox.');
      return;
    }
    // For per-device deploy we DON'T need the preview to have resolved
    // a device — we already know the device_id from the caller. Preview
    // is only there to prefill the target BIOS version.
    if (!initialSelection?.per_device && !Object.keys(previews).length) {
      toast.error('Run Preview first — need at least one row previewed.');
      return;
    }
    // P0 validation — "Available with deadline" needs a picked deadline
    // datetime. datetime-local inputs produce "YYYY-MM-DDTHH:MM" which
    // Graph accepts once we append a UTC offset. We send the raw local
    // ISO plus use_local_time=true so Intune expands to device TZ.
    if (assignmentIntent === 'available_with_deadline' && !deadlineDateTime) {
      toast.error('Pick a deadline date/time for "Available with deadline".');
      return;
    }
    // Resolve grace period → minutes int or null.
    let gracePeriod = null;
    if (gracePresetMinutes === 'custom') {
      const parsed = parseInt(customGraceMinutes, 10);
      if (!Number.isFinite(parsed) || parsed < 0) {
        toast.error('Custom grace period must be a non-negative number of minutes.');
        return;
      }
      gracePeriod = parsed;
    } else if (gracePresetMinutes !== 'none') {
      gracePeriod = parseInt(gracePresetMinutes, 10);
    }
    const countdownIntOrNull = gracePeriod != null && countdownMinutes
      ? parseInt(countdownMinutes, 10)
      : null;
    const snoozeIntOrNull = gracePeriod != null && allowSnooze && snoozeMinutes
      ? parseInt(snoozeMinutes, 10)
      : null;
    // datetime-local returns e.g. "2026-08-15T09:00" — pass through
    // with :00Z appended so it's a valid Edm.DateTimeOffset. Graph
    // rejects bare "2026-08-15T09:00:00" (no offset) with
    // ModelValidationFailure — the backend also normalises defensively
    // but the frontend sends the canonical form here.
    const isoDeadline = assignmentIntent === 'available_with_deadline' && deadlineDateTime
      ? (deadlineDateTime.length === 16
          ? `${deadlineDateTime}:00Z`
          : (/[zZ]$|[+\-]\d{2}:?\d{2}$/.test(deadlineDateTime)
              ? deadlineDateTime
              : `${deadlineDateTime}Z`))
      : null;

    setDeploying(true);
    let succeeded = 0;
    let failed = 0;
    const headers = await authHeaders(getAccessToken);
    for (let i = 0; i < selection.length; i += 1) {
      const row = selection[i];
      const preview = previews[i];
      if (!row.manufacturer || !row.model_family) continue;

      // Per-device path: use the caller's device_id directly, don't
      // require preview.devices to include it. The preview endpoint's
      // job here is only to hand us the latest_download_url + hashes
      // so we know what BIOS to package.
      const isPerDevice = !!initialSelection?.per_device && !!initialSelection?.device_id;
      let deviceIds;
      if (isPerDevice) {
        deviceIds = [initialSelection.device_id];
      } else if (preview?.devices?.length) {
        deviceIds = preview.devices.map((d) => d.id);
      } else {
        continue;
      }

      try {
        await axios.post(
          `${API}/os-patch/firmware/deployments`,
          {
            manufacturer: row.manufacturer,
            model_family: row.model_family,
            target_version: row.target_version || preview?.latest_version,
            download_url: preview?.latest_download_url || initialSelection?.latest_download_url,
            catalog_sha256: preview?.latest_sha256,
            catalog_md5: preview?.latest_md5,
            catalog_sha1_b64: preview?.latest_sha1_b64,
            device_ids: deviceIds,
            status_filter: statusFilter,
            per_device: isPerDevice,
            device_name: initialSelection?.device_name,
            triggered_from: isPerDevice ? 'firmware_report_tab' : 'deployments_tab',
            ms_confirm_accepted: (row.manufacturer || '').toLowerCase().startsWith('microsoft'),
            // P0 2026-07-22
            assignment_intent: assignmentIntent,
            deadline_datetime: isoDeadline,
            use_local_time: true,
            restart_grace_period_minutes: gracePeriod,
            restart_countdown_display_minutes: countdownIntOrNull,
            restart_snooze_duration_minutes: snoozeIntOrNull,
            // Phase 5, Workstream D 2026-07-24 — PSADT deferral
            // deadlines (only sent when the admin ticked the new
            // default intent). Empty string → let backend auto-fill.
            psadt_deadline_at: (assignmentIntent === 'psadt_deferral' && psadtDeadlineAt)
              ? (psadtDeadlineAt.length === 16 ? `${psadtDeadlineAt}:00Z` : psadtDeadlineAt)
              : null,
            intune_deadline_at: (assignmentIntent === 'psadt_deferral' && intuneDeadlineAt)
              ? (intuneDeadlineAt.length === 16 ? `${intuneDeadlineAt}:00Z` : intuneDeadlineAt)
              : null,
            // 2026-08-06 — admin-pickable spacing between deferral prompts.
            defer_interval_hours: assignmentIntent === 'psadt_deferral'
              ? (parseInt(deferIntervalHours, 10) || 24)
              : null,
            current_version: preview?.current_version || preview?.devices?.[0]?.bios_version || null,
          },
          { headers },
        );
        succeeded += 1;
      } catch (e) {
        failed += 1;
        // FastAPI/Pydantic validation errors surface as `detail: [{loc, msg, type}]`
        // (an array of objects) — stringifying directly renders "[object Object]".
        // Extract the human-readable slice so operators see the real reason.
        const detail = e?.response?.data?.detail;
        let reason;
        if (typeof detail === 'string') {
          reason = detail;
        } else if (Array.isArray(detail)) {
          reason = detail
            .map((d) => (d?.msg ? `${(d.loc || []).join('.')}: ${d.msg}` : JSON.stringify(d)))
            .join('; ');
        } else if (detail && typeof detail === 'object') {
          reason = JSON.stringify(detail);
        } else {
          reason = e?.message || 'Unknown error';
        }
        toast.error(`Deploy failed for ${row.manufacturer} ${row.model_family}: ${reason}`);
      }
    }
    setDeploying(false);
    if (succeeded) {
      toast.success(`${succeeded} firmware deployment${succeeded === 1 ? '' : 's'} queued.`);
      onCreated?.();
    }
    if (!succeeded && !failed) {
      toast.info('Nothing to deploy — every preview returned zero devices.');
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur flex items-center justify-center p-4" data-testid="firmware-deployment-wizard">
      <Card className="alice-popup w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col bg-[#0e162a] border-[rgba(229,239,250,0.15)]">
        <div className="p-5 border-b border-[rgba(229,239,250,0.1)] flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Rocket className="w-5 h-5 text-cyan-400" />
            <h2 className="text-lg font-semibold text-white">
              {isSingleDevice ? 'Package & Deploy BIOS to Single Device' : 'New Firmware Deployment'}
            </h2>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="firmware-deployment-wizard-close">
            <X className="w-4 h-4 text-white/70" />
          </Button>
        </div>

        <div className="flex-1 overflow-auto p-5 space-y-4">
          {isSingleDevice && initialSelection?.device_name && (
            <Card className="bg-purple-500/10 border-purple-500/30">
              <CardContent className="p-3 text-sm text-white/90 flex items-center gap-2">
                <HardDrive className="w-4 h-4 text-purple-300" />
                Deploying to single device: <span className="font-mono text-white">{initialSelection.device_name}</span>
              </CardContent>
            </Card>
          )}

          {!isSingleDevice && (
            <div className="flex items-center gap-3">
              <label className="text-white/80 text-sm flex items-center gap-2 cursor-pointer" data-testid="firmware-deploy-bulk-toggle">
                <input
                  type="checkbox"
                  checked={bulkMode}
                  onChange={(e) => {
                    setBulkMode(e.target.checked);
                    if (!e.target.checked) setSelection((prev) => prev.slice(0, 1));
                  }}
                  className="rounded bg-white/10"
                />
                Bulk mode — package multiple (manufacturer, model) tuples
              </label>
            </div>
          )}

          <div className="space-y-3">
            {/* Debug pill — visible in dev to confirm the wizard has
                loaded manufacturer/model data. Safe to keep in prod;
                removes ambiguity when dropdowns look empty. */}
            <div className="text-xs text-white/60" data-testid="firmware-deploy-debug-state">
              {manufacturerOptions.length} manufacturer{manufacturerOptions.length === 1 ? '' : 's'} · {(filterOptions.model_families || []).length} model{(filterOptions.model_families || []).length === 1 ? '' : 's'} loaded
            </div>
            {selection.map((row, idx) => (
              <div
                key={idx}
                className="grid grid-cols-1 md:grid-cols-4 gap-2 items-start bg-white/5 p-3 rounded-lg border border-white/10"
                data-testid={`firmware-deploy-row-${idx}`}
              >
                <div>
                  <label className="text-white/60 text-xs uppercase tracking-wide">Manufacturer</label>
                  <select
                    value={row.manufacturer}
                    onChange={(e) => updateRow(idx, 'manufacturer', e.target.value)}
                    disabled={isSingleDevice}
                    className="w-full mt-1 rounded bg-white/10 border border-white/20 text-white p-2 text-sm"
                    data-testid={`firmware-deploy-manufacturer-${idx}`}
                  >
                    <option value="" style={{ color: '#1e293b', background: '#fff' }}>Select…</option>
                    {(manufacturerOptions || []).map((m) => (
                      <option key={m} value={m} style={{ color: '#1e293b', background: '#fff' }}>{m}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-white/60 text-xs uppercase tracking-wide">Model Family</label>
                  <select
                    value={row.model_family}
                    onChange={(e) => updateRow(idx, 'model_family', e.target.value)}
                    disabled={isSingleDevice || !row.manufacturer}
                    className="w-full mt-1 rounded bg-white/10 border border-white/20 text-white p-2 text-sm"
                    data-testid={`firmware-deploy-model-${idx}`}
                  >
                    <option value="" style={{ color: '#1e293b', background: '#fff' }}>Select…</option>
                    {familiesFor(row.manufacturer).map((f) => (
                      <option key={f} value={f} style={{ color: '#1e293b', background: '#fff' }}>{f}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-white/60 text-xs uppercase tracking-wide">Target BIOS (optional)</label>
                  <Input
                    value={row.target_version || ''}
                    onChange={(e) => updateRow(idx, 'target_version', e.target.value)}
                    placeholder="auto (latest)"
                    className="mt-1 bg-white/10 border-white/20 text-white text-sm font-mono h-9"
                    data-testid={`firmware-deploy-target-version-${idx}`}
                  />
                </div>
                <div className="flex items-end gap-2 h-full">
                  {previews[idx] && (
                    <div className="flex-1 text-xs text-white/80">
                      <div className="font-mono text-cyan-300">
                        {previews[idx].latest_version || '—'}
                      </div>
                      <div>
                        {initialSelection?.per_device
                          ? `1 device (${initialSelection.device_name || 'this device'})`
                          : `${previews[idx].devices?.length || 0} device${previews[idx].devices?.length === 1 ? '' : 's'} affected`}
                      </div>
                    </div>
                  )}
                  {bulkMode && selection.length > 1 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeRow(idx)}
                      className="text-rose-300 hover:bg-rose-500/10 h-9"
                      data-testid={`firmware-deploy-remove-row-${idx}`}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
            {bulkMode && (
              <Button
                variant="outline"
                size="sm"
                onClick={addRow}
                className="border-cyan-400 text-cyan-300 hover:bg-cyan-500/10"
                data-testid="firmware-deploy-add-row"
              >
                + Add another (manufacturer, model)
              </Button>
            )}
          </div>

          {!isSingleDevice && (
            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-white/60 text-sm">Include devices in status:</span>
              {[
                { k: 'missing_one', label: 'Missing 1' },
                { k: 'missing_multiple', label: 'Missing Multiple' },
              ].map((opt) => (
                <label key={opt.k} className="text-white/80 text-sm flex items-center gap-1 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={statusFilter.includes(opt.k)}
                    onChange={(e) => {
                      if (e.target.checked) setStatusFilter((prev) => [...prev, opt.k]);
                      else setStatusFilter((prev) => prev.filter((s) => s !== opt.k));
                    }}
                    className="rounded bg-white/10"
                    data-testid={`firmware-deploy-status-${opt.k}`}
                  />
                  {opt.label}
                </label>
              ))}
            </div>
          )}

          {/* Deploy-as selector — 2026-07-24 (Phase 5, Workstream D):
              replaces the P0 3-intent selector with a single PSADT
              deferral card. The three Phase 4 intents are still valid
              server-side but no longer surfaced in the wizard (per
              customer request 2026-07-24 — "everyone gets the new
              PSADT-deferral flow with a Fallback Intune deadline
              behind it"). */}
          <Card className="bg-cyan-500/5 border-cyan-500/20">
            <CardContent className="p-3 space-y-3">
              <div className="flex items-center gap-2 text-cyan-300 text-sm font-medium">
                <Clock className="w-4 h-4" /> Deployment UX — PSADT deferrals + Intune backstop
              </div>

              <div
                className="rounded-lg border border-cyan-400 bg-cyan-500/10 p-3"
                data-testid="firmware-deploy-intent-psadt_deferral"
              >
                <div className="text-white text-sm font-medium">Interactive Install / Defer prompt (PSADT built-in)</div>
                <p className="text-white/70 text-xs mt-1 leading-relaxed">
                  End-user sees PSADT&apos;s built-in Welcome — <b>Install Now</b> or <b>Defer</b>.
                  Up to <b>3 deferrals</b>, spaced by the interval below (Intune retries inside
                  the window exit as Deferred without re-prompting or burning a deferral). The prompt
                  stays on screen for up to 4 hours until it is answered; an unanswered prompt, or no signed-in
                  user to show it to, does <b>not</b> use a deferral. Once all 3 are used the user gets a final
                  prompt with a <b>60-minute countdown</b> before the install starts.
                  A Defer exits with code <span className="font-mono">1602</span> and an unanswered prompt with
                  {' '}<span className="font-mono">1223</span>; Intune retries both and ALICE reports them as
                  {' '}<b>Deferred</b>. Intune stays set to
                  <span className="font-mono"> required</span> with <b>no time gating</b> —
                  every check-in retries the install until it succeeds; that&apos;s the real backstop.
                </p>
                <p className="text-white/50 text-[11px] mt-2">
                  Deferral state is tracked by PSADT under
                  <span className="font-mono"> HKLM\Software\PSAppDeployToolkit</span>
                  {' '}(keyed by the deployment&apos;s unique InstallName — each redeploy gets a
                  <span className="font-mono"> _R1/_R2…</span> revision suffix so counts never carry over).
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-1">
                <div>
                  <label className="text-white/60 text-xs uppercase tracking-wide">
                    Defer spacing (hours)
                  </label>
                  <Input
                    type="number"
                    min="1"
                    max="168"
                    value={deferIntervalHours}
                    onChange={(e) => setDeferIntervalHours(e.target.value)}
                    className="mt-1 bg-white/10 border-white/20 text-white text-sm h-9"
                    data-testid="firmware-deploy-defer-interval"
                  />
                  <p className="text-white/50 text-[11px] mt-1">
                    Default: 24 h. Minimum time between deferral prompts — Intune
                    retries inside the window exit as Deferred without re-prompting.
                  </p>
                </div>
                <div>
                  <label className="text-white/60 text-xs uppercase tracking-wide">
                    PSADT deadline
                  </label>
                  <Input
                    type="datetime-local"
                    value={psadtDeadlineAt}
                    onChange={(e) => setPsadtDeadlineAt(e.target.value)}
                    className="mt-1 bg-white/10 border-white/20 text-white text-sm h-9"
                    data-testid="firmware-deploy-psadt-deadline"
                  />
                  <p className="text-white/50 text-[11px] mt-1">
                    Default: T+3 days @ 16:00. Shown to the user as &ldquo;Installation required by&rdquo;.
                    Not enforced on the device: the deferral count and final countdown decide when the install starts.
                  </p>
                </div>
                <div>
                  <label className="text-white/60 text-xs uppercase tracking-wide">
                    Intune backstop (informational)
                  </label>
                  <Input
                    type="datetime-local"
                    value={intuneDeadlineAt}
                    onChange={(e) => setIntuneDeadlineAt(e.target.value)}
                    className="mt-1 bg-white/10 border-white/20 text-white text-sm h-9"
                    data-testid="firmware-deploy-intune-backstop"
                  />
                  <p className="text-white/50 text-[11px] mt-1">
                    <b>Not sent to Graph</b> — persisted for audit only. Intune&apos;s own
                    retry loop (fires on every device check-in when the app hasn&apos;t
                    installed yet) is the real backstop. A future <span className="font-mono">deadlineDateTime</span>
                    would block execution, so we don&apos;t set one.
                  </p>
                </div>
              </div>

              <div className="text-white/60 text-[11px] pt-1 border-t border-white/10">
                Max deferrals: <span className="font-mono text-white/80">3</span>
                {' · '}Spacing: <span className="font-mono text-white/80">{parseInt(deferIntervalHours, 10) || 24} h</span>
                {' · '}Defer / unanswered exit codes: <span className="font-mono text-white/80">1602 / 1223</span> (shown as Deferred)
                {' · '}Final countdown: <span className="font-mono text-white/80">60 min</span>
                {' · '}Tracked under: <span className="font-mono text-white/80">HKLM\Software\PSAppDeployToolkit</span>
                {' · '}Intune notifications: <span className="font-mono text-white/80">hidden</span>
                {' '}(PSADT owns the UX)
              </div>
            </CardContent>
          </Card>

          {/* Legacy Phase 4 "Available / Available with deadline / Required"
              intent selector removed 2026-07-24. Keeping the block below
              (rendered only when the URL contains ?legacy_intent=1) as a
              hidden escape hatch for edge-case admins hitting the API
              directly. Not surfaced in normal navigation. */}
          {new URLSearchParams(window.location.search).get('legacy_intent') === '1' && (
          <Card className="bg-white/[0.02] border-white/10">
            <CardContent className="p-3 space-y-3">
              <div className="flex items-center gap-2 text-white/60 text-xs font-medium">
                <Clock className="w-4 h-4" /> Legacy Deploy-as (hidden — ?legacy_intent=1)
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                {[
                  {
                    v: 'available',
                    title: 'Available',
                    subtitle: 'User-initiated via Company Portal. No forced reboot.',
                  },
                  {
                    v: 'available_with_deadline',
                    title: 'Available with deadline',
                    subtitle: 'Shown in Portal, becomes Required at your deadline (countdown popup).',
                  },
                  {
                    v: 'required',
                    title: 'Required',
                    subtitle: 'Admin-forced. Installs on next Intune sync — reboot may be immediate.',
                  },
                ].map((opt) => (
                  <label
                    key={opt.v}
                    className={`rounded-lg border p-3 cursor-pointer transition-colors ${
                      assignmentIntent === opt.v
                        ? 'border-cyan-400 bg-cyan-500/10'
                        : 'border-white/15 hover:border-white/25'
                    }`}
                    data-testid={`firmware-deploy-intent-${opt.v}`}
                  >
                    <input
                      type="radio"
                      name="firmware-deploy-intent"
                      className="sr-only"
                      value={opt.v}
                      checked={assignmentIntent === opt.v}
                      onChange={() => setAssignmentIntent(opt.v)}
                    />
                    <div className="flex items-center gap-2">
                      <span
                        className={`w-3 h-3 rounded-full border ${
                          assignmentIntent === opt.v ? 'bg-cyan-400 border-cyan-400' : 'border-white/40'
                        }`}
                      />
                      <span className="text-white text-sm font-medium">{opt.title}</span>
                    </div>
                    <p className="text-white/70 text-xs mt-1">{opt.subtitle}</p>
                  </label>
                ))}
              </div>

              {assignmentIntent === 'available_with_deadline' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-1">
                  <div>
                    <label className="text-white/60 text-xs uppercase tracking-wide">
                      Deadline (device local time)
                    </label>
                    <Input
                      type="datetime-local"
                      value={deadlineDateTime}
                      onChange={(e) => setDeadlineDateTime(e.target.value)}
                      className="mt-1 bg-white/10 border-white/20 text-white text-sm h-9"
                      data-testid="firmware-deploy-deadline-datetime"
                    />
                    <p className="text-white/50 text-[11px] mt-1">
                      Users can install any time before this. Intune enforces on/after this datetime.
                    </p>
                  </div>
                </div>
              )}

              {(assignmentIntent === 'available_with_deadline' || assignmentIntent === 'required') && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-1 border-t border-white/10">
                  <div>
                    <label className="text-white/60 text-xs uppercase tracking-wide">
                      Restart grace period
                    </label>
                    <select
                      value={gracePresetMinutes}
                      onChange={(e) => setGracePresetMinutes(e.target.value)}
                      className="w-full mt-1 rounded bg-white/10 border border-white/20 text-white p-2 text-sm h-9"
                      data-testid="firmware-deploy-grace-preset"
                    >
                      <option value="none" style={{ color: '#1e293b', background: '#fff' }}>Intune default</option>
                      <option value="15" style={{ color: '#1e293b', background: '#fff' }}>15 minutes</option>
                      <option value="60" style={{ color: '#1e293b', background: '#fff' }}>1 hour</option>
                      <option value="240" style={{ color: '#1e293b', background: '#fff' }}>4 hours</option>
                      <option value="1440" style={{ color: '#1e293b', background: '#fff' }}>24 hours</option>
                      <option value="custom" style={{ color: '#1e293b', background: '#fff' }}>Custom…</option>
                    </select>
                    {gracePresetMinutes === 'custom' && (
                      <Input
                        type="number"
                        min={0}
                        placeholder="minutes"
                        value={customGraceMinutes}
                        onChange={(e) => setCustomGraceMinutes(e.target.value)}
                        className="mt-2 bg-white/10 border-white/20 text-white text-sm h-9"
                        data-testid="firmware-deploy-grace-custom"
                      />
                    )}
                  </div>
                  {gracePresetMinutes !== 'none' && (
                    <>
                      <div>
                        <label className="text-white/60 text-xs uppercase tracking-wide">
                          Countdown before restart
                        </label>
                        <Input
                          type="number"
                          min={0}
                          value={countdownMinutes}
                          onChange={(e) => setCountdownMinutes(e.target.value)}
                          className="mt-1 bg-white/10 border-white/20 text-white text-sm h-9"
                          data-testid="firmware-deploy-countdown-min"
                        />
                        <p className="text-white/50 text-[11px] mt-1">Minutes.</p>
                      </div>
                      <div>
                        <label className="text-white/60 text-xs uppercase tracking-wide">
                          Snooze
                        </label>
                        <div className="mt-1 flex items-center gap-2">
                          <label className="flex items-center gap-2 text-white/80 text-sm cursor-pointer">
                            <input
                              type="checkbox"
                              checked={allowSnooze}
                              onChange={(e) => setAllowSnooze(e.target.checked)}
                              className="rounded bg-white/10"
                              data-testid="firmware-deploy-allow-snooze"
                            />
                            Allow user to snooze
                          </label>
                        </div>
                        {allowSnooze && (
                          <Input
                            type="number"
                            min={0}
                            value={snoozeMinutes}
                            onChange={(e) => setSnoozeMinutes(e.target.value)}
                            className="mt-2 bg-white/10 border-white/20 text-white text-sm h-9"
                            data-testid="firmware-deploy-snooze-min"
                            placeholder="snooze minutes"
                          />
                        )}
                      </div>
                    </>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
          )}

          {anyMicrosoft && (
            <Card className="bg-amber-500/10 border-amber-500/30">
              <CardContent className="p-3 text-sm text-white/90 space-y-2">
                <div className="flex items-center gap-2 text-amber-300 font-medium">
                  <AlertTriangle className="w-4 h-4" /> Microsoft Surface payload — proceed with EXTREME CAUTION
                </div>
                <div className="text-xs">
                  Microsoft Surface .msi files are combined driver + firmware bundles.
                  Microsoft&apos;s recommended path is Windows Update + Intune driver/firmware
                  profiles, not Win32 app packaging. Bad drivers can brick a Surface — you
                  are on the hook.
                </div>
                <label className="flex items-center gap-2 text-white/90 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={msConfirmed}
                    onChange={(e) => setMsConfirmed(e.target.checked)}
                    className="rounded bg-white/10"
                    data-testid="firmware-deploy-ms-confirm"
                  />
                  I understand this includes drivers and firmware; proceed anyway.
                </label>
              </CardContent>
            </Card>
          )}

          {(Object.keys(previews).length > 0 || initialSelection?.per_device) && (
            <Card className="bg-emerald-500/5 border-emerald-500/20">
              <CardContent className="p-3 text-sm text-white/90 flex items-center justify-between">
                {initialSelection?.per_device ? (
                  <span>
                    <span className="font-medium text-emerald-300">1</span> device
                    ({initialSelection.device_name || 'this device'}) will be targeted.
                  </span>
                ) : (
                  <span>
                    <span className="font-medium text-emerald-300">{totalDevices}</span> device{totalDevices === 1 ? '' : 's'} across{' '}
                    <span className="font-medium text-emerald-300">
                      {Object.values(previews).filter((p) => p?.devices?.length > 0).length}
                    </span>{' '}
                    model families will be targeted.
                  </span>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        <div className="p-4 border-t border-[rgba(229,239,250,0.1)] flex items-center justify-end gap-2 bg-[rgba(229,239,250,0.03)]">
          <Button variant="ghost" onClick={onClose} className="text-white/80">Cancel</Button>
          <Button
            variant="outline"
            onClick={handlePreview}
            disabled={previewLoading}
            className="border-cyan-400 text-cyan-300 hover:bg-cyan-500/10"
            data-testid="firmware-deploy-preview-btn"
          >
            {previewLoading ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Loading…</> : 'Preview'}
          </Button>
          <Button
            variant="outline"
            onClick={handlePreviewPsadt}
            disabled={psadtPreviewLoading}
            className="border-fuchsia-400 text-fuchsia-300 hover:bg-fuchsia-500/10"
            data-testid="firmware-deploy-preview-psadt-btn"
            title="Render the Invoke-AppDeployToolkit.ps1 that would be shipped, without touching Intune."
          >
            {psadtPreviewLoading
              ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Rendering…</>
              : <><FileCode2 className="w-4 h-4 mr-2" />Preview PSADT script</>}
          </Button>
          <Button
            onClick={handleDeploy}
            disabled={
              deploying
              || (!initialSelection?.per_device && Object.keys(previews).length === 0)
            }
            className="bg-cyan-500 hover:bg-cyan-600 text-white font-semibold"
            data-testid="firmware-deploy-submit"
          >
            <Rocket className="w-4 h-4 mr-2" />
            {deploying ? 'Deploying…' : 'Package & Deploy'}
          </Button>
        </div>
      </Card>

      {/* Preview PSADT script modal (2026-07-23) — shows the rendered
          Invoke-AppDeployToolkit.ps1 with metadata + install block
          + uninstall no-op. Two tabs: 'ALICE-injected sections' (focused
          view of what actually differs from the vanilla PSADT template)
          and 'Full script' (raw). Both have copy-to-clipboard buttons. */}
      {psadtPreviewOpen && (
        <div
          className="fixed inset-0 z-[60] bg-black/80 backdrop-blur flex items-center justify-center p-4"
          data-testid="firmware-psadt-preview-modal"
        >
          <Card className="alice-popup w-full max-w-6xl max-h-[92vh] overflow-hidden flex flex-col bg-[#0a1220] border-fuchsia-500/30">
            <div className="p-4 border-b border-white/10 flex items-center justify-between bg-fuchsia-500/5">
              <div className="flex items-center gap-3">
                <FileCode2 className="w-5 h-5 text-fuchsia-300" />
                <div>
                  <div className="text-white font-semibold">Preview PSADT script</div>
                  <div className="text-white/60 text-xs">
                    Rendered <span className="font-mono">Invoke-AppDeployToolkit.ps1</span> — nothing has been packaged or uploaded yet.
                  </div>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setPsadtPreviewOpen(false)}
                data-testid="firmware-psadt-preview-close"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {psadtPreviewLoading && (
                <div className="flex items-center gap-2 text-white/70">
                  <RefreshCw className="w-4 h-4 animate-spin" /> Rendering PSADT script…
                </div>
              )}
              {psadtPreviewError && (
                <Card className="bg-red-500/10 border-red-500/40">
                  <CardContent className="p-3 text-red-200 text-sm" data-testid="firmware-psadt-preview-error">
                    {psadtPreviewError}
                  </CardContent>
                </Card>
              )}

              {!psadtPreviewLoading && !psadtPreviewError && psadtPreview && (
                <>
                  {/* Metadata summary — what got substituted into $adtSession */}
                  <Card className="bg-fuchsia-500/5 border-fuchsia-500/20">
                    <CardContent className="p-3 space-y-2" data-testid="firmware-psadt-preview-metadata">
                      <div className="text-fuchsia-300 text-xs uppercase tracking-wider font-medium">$adtSession metadata</div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1 text-sm">
                        {Object.entries(psadtPreview.metadata || {}).map(([k, v]) => (
                          <div key={k} className="flex gap-2">
                            <span className="text-white/60 w-36 shrink-0">{k}</span>
                            <span className="text-white font-mono break-all">{v || <em className="text-white/40">(empty)</em>}</span>
                          </div>
                        ))}
                        <div className="flex gap-2 md:col-span-2 pt-1 border-t border-white/10 mt-1">
                          <span className="text-white/60 w-36 shrink-0">Installer file</span>
                          <span className="text-white font-mono break-all">{psadtPreview.installer_filename}</span>
                        </div>
                        <div className="flex gap-2 md:col-span-2">
                          <span className="text-white/60 w-36 shrink-0">On-endpoint log</span>
                          <span className="text-emerald-300 font-mono break-all">
                            C:\Windows\Logs\Software\{psadtPreview.psadt_log_filename}
                          </span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Tabs */}
                  <div className="flex items-center gap-2 border-b border-white/10">
                    {[
                      { k: 'injected', label: 'ALICE-injected sections' },
                      { k: 'full', label: 'Full script' },
                    ].map((t) => (
                      <button
                        key={t.k}
                        onClick={() => setPsadtPreviewTab(t.k)}
                        className={`px-3 py-2 text-sm border-b-2 transition-colors ${
                          psadtPreviewTab === t.k
                            ? 'border-fuchsia-400 text-fuchsia-200'
                            : 'border-transparent text-white/60 hover:text-white/90'
                        }`}
                        data-testid={`firmware-psadt-preview-tab-${t.k}`}
                      >
                        {t.label}
                      </button>
                    ))}
                    <div className="ml-auto flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => copyToClipboard(
                          psadtPreviewTab === 'full' ? psadtPreview.full_script : psadtPreview.install_block,
                          psadtPreviewTab === 'full' ? 'Full script' : 'ALICE install block',
                        )}
                        className="border-white/20 text-white/80 hover:bg-white/10"
                        data-testid="firmware-psadt-preview-copy-btn"
                      >
                        <CopyIcon className="w-3.5 h-3.5 mr-1.5" /> Copy
                      </Button>
                    </div>
                  </div>

                  {psadtPreviewTab === 'injected' ? (
                    <div className="space-y-3">
                      <div>
                        <div className="text-white/60 text-xs uppercase tracking-wider mb-1">
                          Install block <span className="text-white/40 normal-case tracking-normal">— injected at PSADT&apos;s <code className="font-mono">## &lt;Perform Installation tasks here&gt;</code> marker</span>
                        </div>
                        <pre
                          className="bg-black/50 text-emerald-200 text-xs font-mono p-3 rounded border border-white/10 overflow-x-auto max-h-[38vh] whitespace-pre"
                          data-testid="firmware-psadt-preview-install-block"
                        >
{psadtPreview.install_block}
                        </pre>
                      </div>
                      <div>
                        <div className="text-white/60 text-xs uppercase tracking-wider mb-1">
                          Uninstall block <span className="text-white/40 normal-case tracking-normal">— BIOS is firmware-level, no-op</span>
                        </div>
                        <pre
                          className="bg-black/50 text-cyan-200 text-xs font-mono p-3 rounded border border-white/10 overflow-x-auto whitespace-pre"
                          data-testid="firmware-psadt-preview-uninstall-block"
                        >
{psadtPreview.uninstall_block}
                        </pre>
                      </div>
                    </div>
                  ) : (
                    <pre
                      className="bg-black/50 text-white/80 text-xs font-mono p-3 rounded border border-white/10 overflow-auto max-h-[60vh] whitespace-pre"
                      data-testid="firmware-psadt-preview-full-script"
                    >
{psadtPreview.full_script}
                    </pre>
                  )}
                </>
              )}
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────── main tab component ───────────────────────────
export default function FirmwareDeploymentsTab({ initialSelection = null, onWizardConsumed = null }) {
  const { getAccessToken } = useAuth();
  // Read-only OS Patch tenants: hide "+ Firmware Deployment" and the wizard.
  const { canWriteOSPatch } = usePermissions();
  const [deployments, setDeployments] = useState([]);
  const [aggregate, setAggregate] = useState({
    total_deployments: 0, target_devices: 0, installed: 0, pending: 0, deferred: 0, failed: 0,
  });
  const [loading, setLoading] = useState(true);
  const [showWizard, setShowWizard] = useState(false);
  const [autoOpenSelection, setAutoOpenSelection] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const headers = await authHeaders(getAccessToken);
      // Phase 5 REDESIGN Bug Fix (2026-07-29): use the enriched endpoint
      // that returns per-deployment Intune installSummary stats +
      // aggregate KPI values for the top cards.
      const res = await axios.get(
        `${API}/os-patch/firmware/deployments-with-progress`,
        { headers },
      );
      setDeployments(res.data?.deployments || []);
      setAggregate(res.data?.aggregate || {
        total_deployments: 0, target_devices: 0, installed: 0, pending: 0, deferred: 0, failed: 0,
      });
    } catch (e) {
      toast.error(`Failed to load firmware deployments: ${e?.response?.data?.detail || e.message}`);
      setDeployments([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Auto-refresh while any deployment is in-flight (queued → completed).
  useEffect(() => {
    const anyInFlight = deployments.some((d) =>
      ['queued', 'downloading', 'packaging', 'uploading_to_intune', 'assigning'].includes(d.status),
    );
    if (!anyInFlight) return undefined;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [deployments]);

  // Auto-open wizard when Reports → Firmware page hands us an initialSelection.
  useEffect(() => {
    if (initialSelection) {
      setAutoOpenSelection(initialSelection);
      setShowWizard(true);
    }
  }, [initialSelection]);

  return (
    <div data-testid="firmware-deployments-tab">
      {/* KPI cards — Total Deployments, Target Devices, Installed, Pending, Deferred, Failed */}
      <div className="alice-kpi-row grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-4" data-testid="firmware-deployments-kpi-cards">
        <FwDepKpiCard label="Total Deployments" value={aggregate.total_deployments} accentColor="#00D4E4" icon={Rocket} testId="fw-kpi-total-deployments" />
        <FwDepKpiCard label="Target Devices" value={aggregate.target_devices} accentColor="#8B5CF6" icon={Users} testId="fw-kpi-target-devices" />
        <FwDepKpiCard label="Installed" value={aggregate.installed} accentColor="#22C55E" icon={CheckCircle} testId="fw-kpi-installed" />
        <FwDepKpiCard label="Pending" value={aggregate.pending} accentColor="#F59E0B" icon={Clock} testId="fw-kpi-pending" />
        <FwDepKpiCard label="Deferred" value={aggregate.deferred || 0} accentColor="#38BDF8" icon={PauseCircle} testId="fw-kpi-deferred" />
        <FwDepKpiCard label="Failed" value={aggregate.failed} accentColor="#EF4444" icon={AlertTriangle} testId="fw-kpi-failed" />
      </div>

      <Card className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border-[rgba(229,239,250,0.1)] mb-4">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Cpu className="w-5 h-5 text-cyan-400 mt-0.5" />
            <div>
              <p className="text-white font-medium">Firmware BIOS Deployments</p>
              <p className="text-white/90 text-sm mt-1">
                Package the latest OEM BIOS payload as a Win32 Intune app (reusing the
                same PSADT/intunewin pipeline as Apps Management) and assign to devices
                on old firmware. Deployments follow a fixed 4-phase rollout —
                Test → Phase 1 (10 %) → Phase 2 (50 %) → Phase 3 (100 %) — with
                <code className="text-cyan-300 mx-1 font-mono">ALICE-app-{'{Mfr}'}-{'{Model}'}-{'{Ver}'}-Phase-{'{...}'}</code>
                Entra ID groups auto-created per phase.
                <br />
                <span className="text-amber-300">Microsoft Surface payloads bundle drivers with firmware —
                deploy with extreme caution.</span>
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex items-center gap-3 mb-4">
        {canWriteOSPatch && (
          <Button
            onClick={() => { setAutoOpenSelection(null); setShowWizard(true); }}
            className="bg-cyan-500 hover:bg-cyan-600 text-white font-semibold"
            data-testid="new-firmware-deployment-btn"
          >
            <Rocket className="w-4 h-4 mr-2" />
            + Firmware Deployment
          </Button>
        )}
        <Button
          variant="ghost"
          onClick={load}
          disabled={loading}
          className="text-white/70 hover:text-white"
          data-testid="firmware-deployments-refresh"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {loading && deployments.length === 0 ? (
        <div className="text-center text-white/70 py-12 flex items-center justify-center gap-2">
          <RefreshCw className="w-4 h-4 animate-spin" /> Loading firmware deployments…
        </div>
      ) : deployments.length === 0 ? (
        <Card className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border-[rgba(229,239,250,0.1)]">
          <CardContent className="p-12 text-center">
            <Shield className="w-12 h-12 text-[#E5EFFA]/30 mx-auto mb-4" />
            <h3 className="text-xl font-medium text-white mb-2">No Firmware Deployments</h3>
            <p className="text-white/90 mb-4">
              {canWriteOSPatch
                ? 'Package the latest BIOS for a model family and assign to devices missing the update.'
                : 'No firmware deployments have been created yet.'}
            </p>
            {canWriteOSPatch && (
              <Button
                onClick={() => { setAutoOpenSelection(null); setShowWizard(true); }}
                className="bg-cyan-500 hover:bg-cyan-600"
                data-testid="new-firmware-deployment-empty-btn"
              >
                <Rocket className="w-4 h-4 mr-2" />
                + Firmware Deployment
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <FirmwareDeploymentsTable
          deployments={deployments}
          onRefresh={load}
          onDelete={load}
          getAccessToken={getAccessToken}
        />
      )}

      {canWriteOSPatch && showWizard && (
        <FirmwareDeploymentWizard
          initialSelection={autoOpenSelection}
          onClose={() => {
            setShowWizard(false);
            setAutoOpenSelection(null);
            onWizardConsumed?.();
          }}
          onCreated={() => {
            setShowWizard(false);
            setAutoOpenSelection(null);
            onWizardConsumed?.();
            load();
          }}
        />
      )}
    </div>
  );
}

// ─────────────────────────── KPI card (Firmware Deployments tab) ────
function FwDepKpiCard({ label, value, accentColor, icon: Icon, testId }) {
  return (
    <div
      className="p-4 rounded-lg border bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] border-[rgba(229,239,250,0.1)]"
      data-testid={testId}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="text-white/80 text-xs font-medium uppercase tracking-wide">{label}</div>
        {Icon && <Icon className="h-4 w-4" style={{ color: accentColor }} />}
      </div>
      <div className="text-2xl font-bold tabular-nums" style={{ color: accentColor }}>
        {value?.toLocaleString?.() || 0}
      </div>
    </div>
  );
}


// ─────────────────────────── Apps-Management-style Firmware Deployments table ────
// Bug fix 2026-07-29 (Bug 4): parity with `frontend/src/pages/DeploymentsPage.jsx`
// — same column layout (Firmware · Version · Stage · In Stage · Device
// Progress · Deployed · Actions) and the same action-button chain
// (Refresh / Members / Details / Go Live / Rollback / Log).
function FirmwareDeploymentsTable({ deployments, onRefresh, onDelete, getAccessToken }) {
  const [expandedRowId, setExpandedRowId] = useState(null);
  const [detailsDeployment, setDetailsDeployment] = useState(null);
  const [membersDeployment, setMembersDeployment] = useState(null);
  const [logDeployment, setLogDeployment] = useState(null);
  const [refreshingId, setRefreshingId] = useState(null);
  const [goLiveId, setGoLiveId] = useState(null);
  const [rollbackId, setRollbackId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  // Read-only OS Patch tenants: hide Refresh status / Go Live / Next Phase /
  // Rollback / Delete. Members, Details and Log stay visible.
  const { canWriteOSPatch } = usePermissions();

  const handleDelete = async (d) => {
    if (!window.confirm(
      `Delete firmware deployment ${d.display_name}?\n\n`
      + `• Any Intune app / phase group already created will be un-assigned first (best-effort).\n`
      + `• The deployment doc + log lines will be permanently removed from ALICE.\n\n`
      + `This cannot be undone.`,
    )) return;
    setDeletingId(d.id);
    try {
      const headers = await authHeaders(getAccessToken);
      // Best-effort rollback (un-assign) before hard-delete.
      try {
        await axios.post(
          `${API}/os-patch/firmware/deployments/${d.id}/rollback`,
          {},
          { headers },
        );
      } catch (_) { /* ignore — best-effort */ }
      await axios.delete(
        `${API}/os-patch/firmware/deployments/${d.id}`,
        { headers },
      );
      toast.success('Deployment deleted');
      onDelete?.();
    } catch (e) {
      toast.error(`Delete failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setDeletingId(null);
    }
  };

  const _relFromNow = (iso) => {
    if (!iso) return '—';
    const t = new Date(iso);
    if (Number.isNaN(t.getTime())) return '—';
    const diff = Date.now() - t.getTime();
    const m = Math.floor(diff / 60000);
    if (m < 1) return 'just now';
    if (m < 60) return `${m}m`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h`;
    return `${Math.floor(h / 24)}d`;
  };

  const handleRefresh = async (d) => {
    setRefreshingId(d.id);
    try {
      const headers = await authHeaders(getAccessToken);
      await axios.post(`${API}/os-patch/firmware/deployments/${d.id}/refresh-status`, {}, { headers });
      toast.success('Status refreshed');
      onRefresh?.();
    } catch (e) {
      toast.error(`Refresh failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setRefreshingId(null);
    }
  };

  const handleGoLive = async (d) => {
    const isTest = !d.phase_slot || d.phase_slot === 'test';
    if (!window.confirm(
      `Progress ${d.display_name} to the next phase?\n\n`
      + `Current phase: ${d.phase_slot || 'test'}\n`
      + `ALICE calculates the devices for each phase (Phase 1 = 10%, Phase 2 = 50%, Phase 3 = 100%).\n`
      + `Small fleets (1-2 eligible devices) skip straight to Phase 3 and complete.`
    )) return;
    setGoLiveId(d.id);
    try {
      const headers = await authHeaders(getAccessToken);
      const res = await axios.post(
        `${API}/os-patch/firmware/deployments/${d.id}/advance-phase`,
        {},
        { headers },
      );
      const dep = res.data?.deployment || {};
      if (dep.chain_completed) {
        toast.success('All devices already covered by the Test phase — deployment marked complete (LIVE).');
      } else if (dep.skipped_small_fleet) {
        toast.success(`Small fleet — skipped straight to Phase 3 (100%): ${dep.device_count} device(s) queued.`);
      } else {
        toast.success(`Advanced — ${dep.phase_slot || 'next phase'} queued (${dep.device_count ?? '?'} devices).`);
      }
      onRefresh?.();
    } catch (e) {
      toast.error(`${isTest ? 'Go Live' : 'Next phase'} failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setGoLiveId(null);
    }
  };

  const handleRollback = async (d) => {
    if (!window.confirm(
      `Rollback ${d.display_name}?\n\n`
      + `This will un-assign the Intune firmware app from the current phase group.\n`
      + `The devices will stop receiving the BIOS update prompt.`
    )) return;
    setRollbackId(d.id);
    try {
      const headers = await authHeaders(getAccessToken);
      await axios.post(
        `${API}/os-patch/firmware/deployments/${d.id}/rollback`,
        {},
        { headers },
      ).catch(async (err) => {
        if (err?.response?.status === 404) {
          await axios.delete(
            `${API}/os-patch/firmware/deployments/${d.id}`,
            { headers },
          );
        } else {
          throw err;
        }
      });
      toast.success('Rollback initiated');
      onRefresh?.();
    } catch (e) {
      toast.error(`Rollback failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setRollbackId(null);
    }
  };

  return (
    <>
      <div className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] shadow-[0_8px_24px_rgba(0,0,0,0.15)] rounded-lg border border-[rgba(229,239,250,0.1)] overflow-hidden">
        <Table data-testid="firmware-deployments-table">
          <TableHeader>
            <TableRow className="border-[rgba(229,239,250,0.1)] hover:bg-white/[0.04]">
              <TableHead className="text-white w-8"></TableHead>
              <TableHead className="text-white">Firmware</TableHead>
              <TableHead className="text-white">Version</TableHead>
              <TableHead className="text-white">Stage</TableHead>
              <TableHead className="text-white">In stage</TableHead>
              <TableHead className="text-white">Device Progress</TableHead>
              <TableHead className="text-white">Deployed</TableHead>
              <TableHead className="text-right text-white">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {deployments.map((d) => {
              const stageEnteredAt = d.stage_entered_at || d.created_at;
              const inStage = _relFromNow(stageEnteredAt);
              const deployedAt = d.completed_at || d.updated_at;
              const isLivePhase = ['phase1', 'phase2', 'phase3'].includes(d.phase_slot) || d.go_live_status === 'live';
              // 2026-09-16 (Bug 2) — a newer BIOS for this model went LIVE and
              // this version's Intune app was unassigned; it can't be advanced.
              const isSuperseded = Boolean(d.superseded_by_version);
              const canGoLive = d.status === 'completed' && d.phase_slot !== 'phase3' && d.go_live_status !== 'live' && !isSuperseded;
              return (
                <React.Fragment key={d.id}>
                  <TableRow
                    data-testid={`firmware-deployment-row-${d.id}`}
                    className="border-[rgba(229,239,250,0.1)] hover:bg-white/[0.04]"
                  >
                    <TableCell className="p-1 pl-3">
                      <button
                        type="button"
                        onClick={() => setExpandedRowId(expandedRowId === d.id ? null : d.id)}
                        className="text-white/60 hover:text-white"
                        aria-label="Expand"
                        data-testid={`firmware-expand-row-${d.id}`}
                      >
                        {expandedRowId === d.id
                          ? <ChevronDown className="w-4 h-4" />
                          : <ChevronRight className="w-4 h-4" />}
                      </button>
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium text-white flex items-center gap-2">
                          {d.display_name}
                          {/* 2026-08-13 — ALICE-initiated firmware
                              auto-update (mirrors the Apps Management
                              "auto-update" deployment badge). */}
                          {d.triggered_from === 'firmware_auto_update' && (
                            <Badge
                              className="bg-cyan-600 text-white text-[10px]"
                              title="Created automatically by ALICE — a newer BIOS was published in the OEM catalogue and Auto is enabled for this model."
                              data-testid={`firmware-auto-badge-${d.id}`}
                            >
                              Auto
                            </Badge>
                          )}
                          {isSuperseded && (
                            <Badge
                              className="bg-slate-600 text-white text-[10px]"
                              title={`BIOS ${d.superseded_by_version} for this model has gone LIVE. This version's Intune app has been unassigned so devices no longer try to install it.`}
                              data-testid={`firmware-superseded-badge-${d.id}`}
                            >
                              Superseded by {d.superseded_by_version}
                            </Badge>
                          )}
                        </p>
                        <p className="text-xs text-white/70 font-mono">
                          {d.manufacturer} · {d.model_family}
                          {d.intune_app_id && <> · <span className="text-white/50">{d.intune_app_id.slice(0, 8)}…</span></>}
                        </p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary" className="font-mono bg-[rgba(229,239,250,0.1)] text-white w-fit">
                        {d.target_version || '—'}
                      </Badge>
                    </TableCell>
                    <TableCell><StagePill deployment={d} /></TableCell>
                    <TableCell>
                      <span className="text-sm text-white/80">{inStage}</span>
                    </TableCell>
                    <TableCell className="min-w-[180px]">
                      {d.device_stats && (d.device_stats.total > 0 || d.device_count > 0)
                        ? <DeviceProgressBar stats={d.device_stats?.total > 0 ? d.device_stats : { installed: 0, pending: 0, failed: 0, total: d.device_count || 0 }} />
                        : <span className="text-white/50 text-sm">—</span>
                      }
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-white">{_relFromNow(deployedAt)}</span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1 flex-wrap">
                        {canWriteOSPatch && (
                          <Button
                            variant="outline" size="sm"
                            onClick={() => handleRefresh(d)}
                            disabled={refreshingId === d.id}
                            title="Refresh status from Intune"
                            className="bg-[#E5EFFA] text-[#3C02CC] hover:bg-[#BBFF71] font-semibold"
                            data-testid={`firmware-refresh-btn-${d.id}`}
                          >
                            <RefreshCw className={`h-4 w-4 ${refreshingId === d.id ? 'animate-spin' : ''}`} />
                          </Button>
                        )}
                        <Button
                          variant="outline" size="sm"
                          onClick={() => setMembersDeployment(d)}
                          title="View phase group members"
                          className="bg-blue-500 text-white border-blue-600 hover:bg-blue-600 font-semibold"
                          data-testid={`firmware-members-btn-${d.id}`}
                        >
                          <UserPlus className="h-4 w-4 mr-1" /> Members
                        </Button>
                        <Button
                          variant="outline" size="sm"
                          onClick={() => setDetailsDeployment(d)}
                          className="bg-purple-500 text-white border-purple-600 hover:bg-purple-600 font-semibold"
                          data-testid={`firmware-details-btn-${d.id}`}
                        >
                          <BarChart3 className="h-4 w-4 mr-1" /> Details
                        </Button>
                        {canWriteOSPatch && canGoLive && (
                          (!d.phase_slot || d.phase_slot === 'test') ? (
                            <Button
                              size="sm"
                              onClick={() => handleGoLive(d)}
                              disabled={goLiveId === d.id}
                              className="bg-green-600 hover:bg-green-700 text-white font-semibold"
                              data-testid={`firmware-golive-btn-${d.id}`}
                            >
                              <Rocket className="h-3 w-3 mr-1" />
                              {goLiveId === d.id ? 'Advancing…' : 'Go Live'}
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              onClick={() => handleGoLive(d)}
                              disabled={goLiveId === d.id}
                              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold"
                              title="Progress this rollout to the next phase"
                              data-testid={`firmware-nextphase-btn-${d.id}`}
                            >
                              <ChevronRight className="h-3 w-3 mr-1" />
                              {goLiveId === d.id ? 'Advancing…' : 'Next Phase'}
                            </Button>
                          )
                        )}
                        {canWriteOSPatch && isLivePhase && (
                          <Button
                            variant="outline" size="sm"
                            onClick={() => handleRollback(d)}
                            disabled={rollbackId === d.id}
                            className="border-amber-500 text-amber-400 hover:bg-amber-500/15 hover:text-amber-300 font-semibold"
                            title="Rollback firmware deployment"
                            data-testid={`firmware-rollback-btn-${d.id}`}
                          >
                            <Undo2 className="h-4 w-4 mr-1" />
                            {rollbackId === d.id ? 'Rolling back…' : 'Rollback'}
                          </Button>
                        )}
                        <Button
                          variant="outline" size="sm"
                          onClick={() => setLogDeployment(d)}
                          title="View pipeline log"
                          className="bg-slate-700 text-white border-slate-600 hover:bg-slate-600 font-semibold"
                          data-testid={`firmware-log-btn-${d.id}`}
                        >
                          <FileText className="h-4 w-4 mr-1" /> Log
                        </Button>
                        {canWriteOSPatch && (
                          <Button
                            variant="outline" size="sm"
                            onClick={() => handleDelete(d)}
                            disabled={deletingId === d.id}
                            title="Delete this firmware deployment"
                            className="bg-rose-500/90 text-white border-rose-600 hover:bg-rose-600 font-semibold"
                            data-testid={`firmware-delete-btn-${d.id}`}
                          >
                            <Trash className="h-4 w-4 mr-1" />
                            {deletingId === d.id ? 'Deleting…' : 'Delete'}
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                  {expandedRowId === d.id && (
                    <TableRow className="border-[rgba(229,239,250,0.1)] bg-white/[0.02]">
                      <TableCell colSpan={8} className="py-4">
                        <FirmwareRowDetails deployment={d} />
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {logDeployment && (
        <FirmwareLogModal
          deployment={logDeployment}
          onClose={() => setLogDeployment(null)}
        />
      )}
      {detailsDeployment && (
        <FirmwareDetailsModal
          deployment={detailsDeployment}
          onClose={() => setDetailsDeployment(null)}
        />
      )}
      {membersDeployment && (
        <FirmwareMembersModal
          deployment={membersDeployment}
          onClose={() => setMembersDeployment(null)}
          getAccessToken={getAccessToken}
        />
      )}
    </>
  );
}

function FirmwareRowDetails({ deployment }) {
  const perDev = deployment.per_device_status || [];
  return (
    <div className="px-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4 text-sm">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-white/60">Phase slot</div>
          <div className="text-white/90">{deployment.phase_slot || 'ad-hoc'}</div>
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-wider text-white/60">Assignment intent</div>
          <div className="text-white/90">{deployment.assignment_intent || '—'}</div>
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-wider text-white/60">Intune App ID</div>
          <div className="text-white/90 font-mono text-xs break-all">
            {deployment.intune_app_id || '—'}
          </div>
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-wider text-white/60">Entra Group</div>
          <div className="text-white/90 font-mono text-xs break-all">
            {deployment.azure_ad_group_name || '—'}
          </div>
        </div>
      </div>
      {perDev.length > 0 ? (
        <div className="overflow-x-auto border border-white/10 rounded-lg">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase text-white/60 bg-white/5">
                <th className="px-3 py-2 font-medium">Device</th>
                <th className="px-3 py-2 font-medium">State</th>
                <th className="px-3 py-2 font-medium">Last sync</th>
              </tr>
            </thead>
            <tbody>
              {perDev.slice(0, 10).map((d, i) => (
                <tr key={i} className="border-t border-white/5">
                  <td className="px-3 py-2 text-white/90">{d.device_name || '—'}</td>
                  <td className="px-3 py-2 text-white/80">{d.category === 'deferred' ? 'Deferred' : (d.install_state || d.category || '—')}</td>
                  <td className="px-3 py-2 text-white/60 text-xs">{d.last_sync || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-xs text-white/50">No per-device data available yet.</p>
      )}
    </div>
  );
}

function FirmwareLogModal({ deployment, onClose }) {
  const logLines = deployment.log_lines || [];
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="firmware-log-modal">
      <div className="alice-popup bg-slate-950 rounded-lg border border-white/10 max-w-4xl w-full max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <div>
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <FileText className="w-5 h-5 text-cyan-400" /> Deployment Log
            </h3>
            <p className="text-xs text-white/60 mt-0.5 font-mono">{deployment.display_name}</p>
          </div>
          <button onClick={onClose} className="text-white/60 hover:text-white" data-testid="firmware-log-close">
            <X className="w-6 h-6" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 font-mono text-xs bg-black">
          {logLines.length === 0 ? (
            <div className="text-white/60">No log lines captured.</div>
          ) : (
            logLines.map((l, i) => (
              <div key={i} className="whitespace-pre-wrap flex gap-3 py-0.5">
                <span className="text-white/40 shrink-0">{(l.ts || '').replace('T', ' ').slice(0, 19)}</span>
                <span className={
                  l.level === 'error' ? 'text-rose-400'
                    : l.level === 'warn' ? 'text-amber-300'
                      : 'text-white/85'
                }>[{l.level || 'info'}]</span>
                <span className="text-white/90">{l.message}</span>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

function FirmwareDetailsModal({ deployment, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="firmware-details-modal">
      <div className="alice-popup bg-slate-900 rounded-lg border border-white/10 max-w-3xl w-full max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <div>
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-purple-400" /> Deployment Details
            </h3>
            <p className="text-xs text-white/60 mt-0.5">{deployment.display_name}</p>
          </div>
          <button onClick={onClose} className="text-white/60 hover:text-white" data-testid="firmware-details-close">
            <X className="w-6 h-6" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div><span className="text-white/60 uppercase tracking-wide text-[10px] block">Status</span><span className="text-white">{deployment.status}</span></div>
            <div><span className="text-white/60 uppercase tracking-wide text-[10px] block">Phase</span><span className="text-white">{deployment.phase_slot || 'ad-hoc'} (phase {deployment.phase || '—'})</span></div>
            <div><span className="text-white/60 uppercase tracking-wide text-[10px] block">Manufacturer</span><span className="text-white">{deployment.manufacturer}</span></div>
            <div><span className="text-white/60 uppercase tracking-wide text-[10px] block">Model</span><span className="text-white">{deployment.model_family}</span></div>
            <div><span className="text-white/60 uppercase tracking-wide text-[10px] block">Target Version</span><span className="text-white font-mono">{deployment.target_version}</span></div>
            <div><span className="text-white/60 uppercase tracking-wide text-[10px] block">Device Count</span><span className="text-white">{deployment.device_count || 0}</span></div>
            <div><span className="text-white/60 uppercase tracking-wide text-[10px] block">Intune App ID</span><span className="text-white font-mono text-xs break-all">{deployment.intune_app_id || '—'}</span></div>
            <div><span className="text-white/60 uppercase tracking-wide text-[10px] block">Entra Group</span><span className="text-white font-mono text-xs break-all">{deployment.azure_ad_group_name || '—'}</span></div>
          </div>
          {deployment.device_stats && (deployment.device_stats.total > 0) && (
            <div>
              <div className="text-white/60 uppercase tracking-wide text-[10px] mb-1">Intune Install Progress</div>
              <DeviceProgressBar stats={deployment.device_stats} />
            </div>
          )}
          {(deployment.per_device_status || []).length > 0 && (
            <div>
              <div className="text-white/60 uppercase tracking-wide text-[10px] mb-2">Per-device status</div>
              <div className="border border-white/10 rounded max-h-72 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="bg-white/5"><tr>
                    <th className="text-left px-3 py-2 text-white/70">Device</th>
                    <th className="text-left px-3 py-2 text-white/70">State</th>
                    <th className="text-left px-3 py-2 text-white/70">Last sync</th>
                  </tr></thead>
                  <tbody>
                    {(deployment.per_device_status || []).map((d, i) => (
                      <tr key={i} className="border-t border-white/5">
                        <td className="px-3 py-1.5 text-white/90">{d.device_name || '—'}</td>
                        <td className="px-3 py-1.5 text-white/80">{d.category === 'deferred' ? 'Deferred' : (d.install_state || d.category || '—')}</td>
                        <td className="px-3 py-1.5 text-white/60">{d.last_sync || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function FirmwareMembersModal({ deployment, onClose, getAccessToken }) {
  const [members, setMembers] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const headers = await authHeaders(getAccessToken);
        const res = await axios.get(
          `${API}/os-patch/firmware/deployments/${deployment.id}/members`,
          { headers },
        );
        if (mounted) setMembers(res.data?.members || []);
      } catch (e) {
        if (e?.response?.status === 404) {
          if (mounted) setMembers([]);
        } else if (mounted) {
          toast.error(`Failed to load members: ${e?.response?.data?.detail || e.message}`);
        }
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [deployment.id]);
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="firmware-members-modal">
      <div className="alice-popup bg-slate-900 rounded-lg border border-white/10 max-w-2xl w-full max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <div>
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-blue-400" /> Phase Group Members
            </h3>
            <p className="text-xs text-white/60 mt-0.5 font-mono">{deployment.azure_ad_group_name || 'Group not yet created'}</p>
          </div>
          <button onClick={onClose} className="text-white/60 hover:text-white" data-testid="firmware-members-close">
            <X className="w-6 h-6" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-5">
          {loading ? (
            <div className="text-white/60 text-center py-10">Loading…</div>
          ) : (members || []).length === 0 ? (
            <div className="text-white/60 text-sm py-8 text-center">
              No members returned. This may be an ad-hoc deployment (no phase group)
              or the members endpoint is not yet available.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-white/5"><tr>
                <th className="text-left px-3 py-2 text-white/70">Device</th>
                <th className="text-left px-3 py-2 text-white/70">Primary user</th>
                <th className="text-left px-3 py-2 text-white/70">Azure ID</th>
              </tr></thead>
              <tbody>
                {members.map((m, i) => (
                  <tr key={i} className="border-t border-white/5">
                    <td className="px-3 py-1.5 text-white">{m.device_name || '—'}</td>
                    <td className="px-3 py-1.5 text-white/80">{m.user_name || m.user_principal_name || '—'}</td>
                    <td className="px-3 py-1.5 text-white/60 font-mono text-xs break-all">{m.azure_ad_device_id || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
